import React, { useContext, useEffect, useState } from "react";
import Modal from "react-modal/lib/components/Modal";
import HeaderSimple from "../../../../components/header/simple";
import { BtnBack } from "../../../../components/button/button-back";
import Notification from "../../../../components/toast";
import { ContextRegisterProduct } from "../context";
import { Plus, Trash2 } from "lucide-react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import functions from "../../../../util/functions";
import reqProduto from "../../../../services/reqMultiTenant/reqProduto";
import libFloat from "../../../../util/libFloat";
import MessageDialog from "../../../../components/ui/dialog/MessageDialog";
import AlertMessage from "../../../../components/alert-message";

function ModalProductVariants() {
	const { isOpenModalVariants, handleCloseModalVariants, parentProduct } = useContext(ContextRegisterProduct);

	const [variants, setVariants] = useState([]);
	const [grupoConfig, setGrupoConfig] = useState({ id_complementos_grupo: 0, descricao: "" });
	const [isEditMode, setIsEditMode] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showConfirmSave, setShowConfirmSave] = useState(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);

	const parseAndFormatValor = (val) => {
		if (val === undefined || val === null) return "0,00";
		const num = typeof val === "string" ? functions.parseNumber(val) : val;
		return functions.formatarMoeda((num || 0).toFixed(2).replace(".", ""));
	};

	const parseAndFormatQtde = (val) => {
		if (val === undefined || val === null) return "0";
		const num = typeof val === "string" ? functions.parseNumber(val) : val;
		return num.toString().replace(".", ",");
	};

	const validarQtde3Casas = (valor) => {
		if (!valor) return "";
		let v = valor.toString().replace(/\./g, ",");
		v = v.replace(/[^\d,]/g, "");
		const partes = v.split(",");
		if (partes.length > 2) v = partes[0] + "," + partes.slice(1).join("").replace(/,/g, "");
		const novasPartes = v.split(",");
		if (novasPartes.length === 2) v = novasPartes[0] + "," + novasPartes[1].substring(0, 3);
		return v;
	};

	useEffect(() => {
		if (!isOpenModalVariants || !parentProduct) return;

		const carregarGrade = async () => {
			setIsLoading(true);
			try {
				const response = await reqProduto.getGrade(parentProduct.id_produto);
				if (response?.data?.grupo) {
					const { grupo, variacoes } = response.data;
					setGrupoConfig({
						id_complementos_grupo: grupo.id_complementos_grupo,
						descricao: grupo.descricao ?? "",
					});
					setVariants(
						variacoes.map((v) => ({
							id: v.id_complementos_item.toString(),
							id_produto: v.id_produto,
							id_complementos_item: v.id_complementos_item,
							nome: v.nome,
							valor: parseAndFormatValor(v.valor),
							qtatual: parseAndFormatQtde(v.qtatual),
							ativo: v.ativo ?? true,
						}))
					);
					setIsEditMode(true);
				} else {
					setVariants([]);
					setGrupoConfig({ id_complementos_grupo: 0, descricao: "" });
					setIsEditMode(false);
				}
			} catch {
				setVariants([]);
				setIsEditMode(false);
			} finally {
				setIsLoading(false);
			}
		};

		carregarGrade();
	}, [isOpenModalVariants, parentProduct]);

	const handleSafeClose = () => {
		if (variants.length > 0 && !isSaving && !isEditMode) {
			setShowCancelConfirm(true);
		} else {
			handleCloseModalVariants();
		}
	};

	const handleAddRow = () => {
		setVariants((prev) => [
			...prev,
			{
				id: Date.now().toString(),
				id_produto: null,
				id_complementos_item: null,
				nome: `${parentProduct.nome.toUpperCase()} - `,
				valor: parseAndFormatValor(parentProduct.valor),
				qtatual: parseAndFormatQtde(parentProduct.qtatual),
				ativo: true,
			},
		]);
		setTimeout(() => {
			const inputs = document.querySelectorAll('input[placeholder="Nome da Variação..."]');
			const lastInput = inputs[inputs.length - 1];
			if (lastInput) { lastInput.focus(); lastInput.select(); }
		}, 50);
	};

	const handleRemoveRow = (id) => {
		setVariants((prev) => prev.filter((v) => v.id !== id));
	};

	const handleToggleAtivo = (v) => {
		setVariants((prev) =>
			prev.map((item) => (item.id === v.id ? { ...item, ativo: !item.ativo } : item))
		);
	};

	const handleUpdateRow = (id, field, value) => {
		setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
	};

	const handleSave = (e) => {
		e.preventDefault();
		const invalidRow = variants.find((v) => v.ativo && (!v.nome.trim() || v.valor === "" || v.qtatual === ""));
		if (invalidRow) {
			Notification.showToast({ message: "Preencha o Nome, Valor e Estoque de todas as variações ativas.", type: "warning" });
			return;
		}
		const activeVariants = variants.filter((v) => v.ativo);
		if (activeVariants.length === 0 && !isEditMode) {
			Notification.showToast({ message: "Nenhuma variação para salvar.", type: "warning" });
			return;
		}
		setShowConfirmSave(true);
	};

	const totalEstoqueGrade = variants.reduce((sum, v) => {
		const qt = typeof v.qtatual === "string" ? functions.parseNumber(v.qtatual) : v.qtatual;
		return sum + (qt || 0);
	}, 0);

	const executeSave = async () => {
		setShowConfirmSave(false);
		setIsSaving(true);
		try {
			const normalizarValor = (val) => (typeof val === "string" ? libFloat.strBrToCurrency(val) : val || 0);

			if (isEditMode) {
				const payload = {
					id_produto_pai: parentProduct.id_produto,
					produto_pai: {
						...parentProduct,
						valor: normalizarValor(parentProduct.valor),
						custo: normalizarValor(parentProduct.custo),
						lucro: normalizarValor(parentProduct.lucro),
						markup: normalizarValor(parentProduct.markup),
						rendimento: normalizarValor(parentProduct.rendimento),
						descmax: normalizarValor(parentProduct.descmax),
						comissao: normalizarValor(parentProduct.comissao),
						fracao: normalizarValor(parentProduct.fracao),
					},
					grupo: {
						id_complementos_grupo: grupoConfig.id_complementos_grupo,
						descricao: grupoConfig.descricao,
					},
					variacoes: variants.map((v) => ({
						id_produto: v.id_produto ?? null,
						id_complementos_item: v.id_complementos_item ?? null,
						nome: v.nome,
						valor: normalizarValor(v.valor),
						qtatual: normalizarValor(v.qtatual),
						ativo: v.ativo ?? true,
					})),
				};
				await reqProduto.atualizarGrade(payload);
				Notification.showToast({ message: "Grade atualizada com sucesso!", type: "success" });
			} else {
				const payload = {
					produto_pai: {
						...parentProduct,
						valor: normalizarValor(parentProduct.valor),
						custo: normalizarValor(parentProduct.custo),
						lucro: normalizarValor(parentProduct.lucro),
						markup: normalizarValor(parentProduct.markup),
						rendimento: normalizarValor(parentProduct.rendimento),
						descmax: normalizarValor(parentProduct.descmax),
						comissao: normalizarValor(parentProduct.comissao),
						fracao: normalizarValor(parentProduct.fracao),
						qtatual: totalEstoqueGrade,
						qt1: totalEstoqueGrade,
					},
					grupo: {
						descricao: grupoConfig.descricao,
					},
					variacoes: variants.map((v) => ({
						nome: v.nome,
						valor: normalizarValor(v.valor),
						qtatual: normalizarValor(v.qtatual),
					})),
				};
				await reqProduto.gerarGrade(payload);
				Notification.showToast({ message: `Grade com ${variants.length} variações gerada com sucesso!`, type: "success" });
			}
			handleCloseModalVariants();
		} catch (error) {
			const msg = error.response?.data?.message || error.message || "Erro desconhecido ao salvar a grade.";
			Notification.showToast({ message: msg, type: "error" });
		} finally {
			setIsSaving(false);
		}
	};

	const handleGridKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			const currentTd = e.target.closest("td");
			const nextTd = currentTd?.nextElementSibling;
			const nextInput = nextTd?.querySelector("input:not(:disabled)");
			if (nextInput) {
				nextInput.focus(); nextInput.select();
			} else {
				const currentTr = e.target.closest("tr");
				const nextTr = currentTr?.nextElementSibling;
				const firstInputOfNextRow = nextTr?.querySelector("input:not(:disabled)");
				if (firstInputOfNextRow) {
					firstInputOfNextRow.focus(); firstInputOfNextRow.select();
				} else {
					const newId = Date.now().toString();
					setVariants((prev) => [
						...prev,
						{ id: newId, id_produto: null, id_complementos_item: null, nome: `${parentProduct.nome.toUpperCase()} - `, valor: parseAndFormatValor(parentProduct.valor), qtatual: parseAndFormatQtde(parentProduct.qtatual), ativo: true },
					]);
					setTimeout(() => {
						const inputs = document.querySelectorAll('input[placeholder="Nome da Variação..."]');
						const lastInput = inputs[inputs.length - 1];
						if (lastInput) { lastInput.focus(); lastInput.select(); }
					}, 50);
				}
			}
		} else if (e.key === "ArrowDown") {
			e.preventDefault();
			const currentTr = e.target.closest("tr");
			const currentTd = e.target.closest("td");
			const cellIndex = Array.from(currentTr.children).indexOf(currentTd);
			const nextTr = currentTr.nextElementSibling;
			if (nextTr) { const nextInput = nextTr.children[cellIndex]?.querySelector("input:not(:disabled)"); if (nextInput) { nextInput.focus(); nextInput.select(); } }
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			const currentTr = e.target.closest("tr");
			const currentTd = e.target.closest("td");
			const cellIndex = Array.from(currentTr.children).indexOf(currentTd);
			const prevTr = currentTr.previousElementSibling;
			if (prevTr) { const prevInput = prevTr.children[cellIndex]?.querySelector("input:not(:disabled)"); if (prevInput) { prevInput.focus(); prevInput.select(); } }
		}
	};

	if (!parentProduct) return null;

	return (
		<Modal
			isOpen={isOpenModalVariants}
			onRequestClose={handleSafeClose}
			overlayClassName="react-modal-overlay"
			className="modal-content-form modal-product-resize"
			shouldCloseOnOverlayClick={false}
		>
			<div className="flex flex-col h-full bg-white rounded shadow-xl overflow-hidden" style={{ maxHeight: "90vh" }}>
				<HeaderSimple
					title={isEditMode ? "Editar Grade / Variações" : "Gerador de Variações (Grade)"}
					subTitle={`Produto Base: ${parentProduct.nome}`}
					handleCloseModalRegister={handleSafeClose}
				/>

				<div className="py-2 px-3 bg-blue-50 border-b text-xs text-blue-800 shrink-0 flex flex-col md:flex-row justify-between items-center gap-3">
					<div className="flex-1">
						<span className="text-[9px] text-blue-600 block uppercase font-bold tracking-wider leading-none">Produto Principal</span>
						<h3 className="font-bold text-base text-blue-900 leading-tight m-0">{parentProduct.nome}</h3>
					</div>
					<div className="flex gap-4 shrink-0 items-center justify-around">
						<div className="text-center">
							<span className="text-[9px] text-gray-500 block uppercase leading-none">Estoque Pai</span>
							<span className="font-extrabold text-gray-700 text-sm">{parseAndFormatQtde(parentProduct.qtatual)}</span>
						</div>
						<div className="text-center">
							<span className="text-[9px] text-gray-500 block uppercase leading-none">Preço Pai</span>
							<span className="font-extrabold text-gray-700 text-sm">R$ {parseAndFormatValor(parentProduct.valor)}</span>
						</div>
						<div className="text-center bg-blue-100 px-2 py-1 rounded border border-blue-200">
							<span className="text-[9px] text-blue-600 block uppercase font-semibold leading-none">Estoque Total Grade</span>
							<span className="font-black text-blue-900 text-sm">{totalEstoqueGrade}</span>
						</div>
					</div>
				</div>

				{/* Configurações do grupo — visível em ambos os modos */}
				<div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-2 shrink-0">
					<span className="text-xs font-semibold text-gray-600 uppercase tracking-wide shrink-0">Descrição:</span>
					<input
						type="text"
						className="border border-gray-300 rounded px-2 py-0.5 text-sm flex-1 uppercase"
						value={grupoConfig.descricao}
						onChange={(e) => setGrupoConfig((prev) => ({ ...prev, descricao: e.target.value.toUpperCase() }))}
						placeholder="Nome do grupo..."
					/>
				</div>

				{isLoading ? (
					<div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Carregando grade...</div>
				) : (
					<form onSubmit={handleSave} className="flex-1 flex flex-col overflow-hidden h-full">
						<main className="flex-1 flex flex-col min-h-0 p-3 bg-gray-100 overflow-hidden">
							<div className="bg-white border rounded shadow-sm flex flex-col flex-1 min-h-[220px] max-h-[460px] overflow-hidden">
								<div className="overflow-y-auto flex-1">
									<table className="table table-hover mb-0 w-full table-fixed">
										<thead className="color-table sticky top-0 z-10 shadow-sm border-b">
											<tr>
												<th className="text-left font-semibold py-2 px-3 shadow-sm">
													<span className="text-color-table">Nome da Variação (Ex: Camisa - P)</span>
												</th>
												<th className="text-center font-semibold py-2 px-3 w-32 shadow-sm">
													<span className="text-color-table">Preço (R$)</span>
												</th>
												<th className="text-center font-semibold py-2 px-3 w-32 shadow-sm">
													<span className="text-color-table">Estoque</span>
												</th>
												<th className="text-right font-semibold py-2 px-3 w-16 shadow-sm">
													<span className="text-color-table">Ação</span>
												</th>
											</tr>
										</thead>
										<tbody>
											{variants.map((v) => (
												<tr key={v.id} className={`border-b last:border-0 hover:bg-gray-50 transition-opacity ${!v.ativo ? "opacity-50" : ""}`}>
													<td className="p-1.5">
														<input
															type="text"
															className="w-full bg-transparent border border-gray-300 focus:border-blue-500 rounded px-2 py-1 outline-none transition-colors uppercase text-sm disabled:cursor-not-allowed"
															value={v.nome}
															onChange={(e) => handleUpdateRow(v.id, "nome", e.target.value.toUpperCase())}
															onKeyDown={handleGridKeyDown}
															placeholder="Nome da Variação..."
															disabled={!v.ativo}
															required={v.ativo}
														/>
													</td>
													<td className="p-1.5">
														<input
															type="text"
															className="w-full bg-transparent border border-gray-300 focus:border-blue-500 rounded px-2 py-1 outline-none transition-colors text-center text-sm disabled:cursor-not-allowed"
															value={v.valor}
															onChange={(e) => handleUpdateRow(v.id, "valor", functions.formatarMoeda(e.target.value))}
															onFocus={(e) => e.target.select()}
															onKeyDown={handleGridKeyDown}
															disabled={!v.ativo}
															required={v.ativo}
														/>
													</td>
													<td className="p-1.5">
														<input
															type="text"
															className="w-full bg-transparent border border-gray-300 focus:border-blue-500 rounded px-2 py-1 outline-none transition-colors text-center text-sm disabled:cursor-not-allowed"
															value={v.qtatual}
															onChange={(e) => handleUpdateRow(v.id, "qtatual", validarQtde3Casas(e.target.value))}
															onFocus={(e) => e.target.select()}
															onKeyDown={handleGridKeyDown}
															disabled={!v.ativo}
															required={v.ativo}
														/>
													</td>
													<td className="p-1.5 text-right">
														<div className="flex justify-end gap-1">
															{v.id_complementos_item ? (
																<button
																	type="button"
																	className={`btn btn-sm ${v.ativo ? "btn-outline-success" : "btn-outline-danger"} p-1`}
																	onClick={() => handleToggleAtivo(v)}
																	title={v.ativo ? "Desativar variação" : "Ativar variação"}
																>
																	{v.ativo ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
																</button>
															) : (
																<button
																	type="button"
																	className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-100"
																	onClick={() => handleRemoveRow(v.id)}
																	title="Remover Linha"
																>
																	<Trash2 size={16} />
																</button>
															)}
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								<div className="p-2 bg-gray-50 border-t flex justify-center shrink-0">
									<button
										type="button"
										className="btn btn-xs btn-outline-primary flex items-center gap-2 py-1 px-3 text-xs"
										onClick={handleAddRow}
									>
										<Plus size={14} /> Adicionar Variação
									</button>
								</div>
							</div>

							{!isEditMode && (
								<AlertMessage type="info" title="Atenção" className="mt-2 py-2 px-3 shrink-0">
									Ao salvar, o sistema criará <strong>um novo produto</strong> no banco para cada linha, herdando Categoria, Impostos e Custo do produto base. Um grupo de complementos também será criado e vinculado automaticamente.
								</AlertMessage>
							)}
						</main>

						<footer className="mt-auto border-t pt-3 pb-2 px-4 bg-gray-50/80 shrink-0 flex justify-between items-center z-20">
							<BtnBack onClick={handleSafeClose} isSubmitting={isSaving} />
							<div className="flex gap-2 ml-auto">
								<button
									type="submit"
									className="btn btn-success font-medium px-4 py-2 flex items-center justify-center gap-2"
									disabled={isSaving || variants.length === 0}
								>
									{isSaving ? (
										<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{isEditMode ? "Salvando..." : "Gerando Grade..."}</>
									) : isEditMode ? (
										"Salvar Alterações"
									) : (
										`Salvar ${variants.length} Variaç${variants.length === 1 ? "ão" : "ões"}`
									)}
								</button>
							</div>
						</footer>
					</form>
				)}
			</div>

			<MessageDialog
				open={showConfirmSave}
				title={isEditMode ? "Confirmar Alterações" : "Confirmar Geração de Grade"}
				description={isEditMode
					? `Deseja salvar as alterações nas ${variants.length} variações?`
					: `Deseja realmente gerar as ${variants.length} variações? Essa ação criará novos produtos e os vinculará ao produto base.`}
				variant="question"
				buttons="confirmCancel"
				onConfirm={executeSave}
				onCancel={() => setShowConfirmSave(false)}
				autoFocus="confirm"
			/>

			<MessageDialog
				open={showCancelConfirm}
				title="Confirmar Cancelamento"
				description="Existem variações não salvas! Deseja realmente cancelar?"
				variant="warning"
				buttons="confirmCancel"
				onConfirm={() => { setShowCancelConfirm(false); handleCloseModalVariants(); }}
				onCancel={() => setShowCancelConfirm(false)}
				autoFocus="cancel"
			/>
		</Modal>
	);
}

export default ModalProductVariants;
